/**
 * TypeScript wire shapes for the /api/intake/ router — Phase 6 (deliver-notion-pm).
 *
 * Mirrors backend/sherlock/schemas/intake.py field-for-field.
 * snake_case on the wire (see .claude/rules/api-contract.md).
 */

// ---------------------------------------------------------------------------
// Attachments (mirrors AttachmentOut in schemas/intake.py)
// ---------------------------------------------------------------------------

export type AttachmentMirrorStatus = 'pending' | 'mirrored' | 'failed' | 'skipped';

export interface IntakeAttachment {
  id: string;
  intake_session_id: string;
  requirement_id: string | null;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  uploaded_by: string | null;
  notion_mirror_status: AttachmentMirrorStatus;
  created_at: string; // ISO 8601
  /** API path, e.g. "/api/attachments/<id>/download/" (JWT required). */
  download_url: string;
}

export interface IntakeAttachmentListResponse {
  results: IntakeAttachment[];
}

/** question_type controls how IntakeWizardPage renders each question. */
export type IntakeQuestionType = 'single-select' | 'multi-select' | 'free-text';

/**
 * Wire status values from the backend.
 * 'questioning' and 'error' are FE-only UI states managed by useIntakeWizard;
 * the four that travel on the wire are 'in_progress', 'preview', 'confirmed', 'abandoned'.
 */
export type IntakeSessionStatus =
  | 'in_progress'
  | 'preview'
  | 'confirmed'
  | 'abandoned'
  // FE-only states (never sent by the backend):
  | 'questioning'
  | 'error';

// ---------------------------------------------------------------------------
// Phase A — step model types (mirrors IntakeStepOut in schemas/intake.py)
// ---------------------------------------------------------------------------

/** Step kinds in the intake agent loop. */
export type IntakeStepKind =
  | 'extract'
  | 'plan'
  | 'recommend'
  | 'synthesize'
  | 'dedupe'
  | 'confirm';

/** Per-step lifecycle status. */
export type IntakeStepStatus = 'pending' | 'running' | 'done' | 'failed';

/** Error category for a failed step. */
export type IntakeErrorKind = 'transient' | 'upstream' | 'internal';

/**
 * One persisted step in the intake session step list.
 * Mirrors IntakeStepOut in backend/sherlock/schemas/intake.py.
 */
export interface IntakeStepOut {
  step_id: string;
  kind: IntakeStepKind;
  round_index: number | null;
  status: IntakeStepStatus;
  error: string | null;
  error_kind: IntakeErrorKind | null;
  attempts: number;
  started_at: string | null; // ISO 8601 datetime string
  finished_at: string | null; // ISO 8601 datetime string
}

// ---------------------------------------------------------------------------
// Phase A — draft list types (mirrors IntakeDraftSummary in schemas/intake.py)
// ---------------------------------------------------------------------------

/**
 * One draft session in the GET /api/intake/drafts/ response.
 * Mirrors IntakeDraftSummary in backend/sherlock/schemas/intake.py.
 */
export interface IntakeDraftSummary {
  id: string; // UUID as string on the wire
  status: 'in_progress' | 'preview';
  initial_request: string;
  projects: string[];
  phase: string;
  round: number;
  title: string | null;
  last_error: string | null;
  failed_step_id: string | null;
  created_at: string; // ISO 8601
  updated_at: string; // ISO 8601
  /** Non-deleted attachments on the draft. */
  attachment_count: number;
}

/**
 * Response envelope for GET /api/intake/drafts/.
 * Mirrors IntakeDraftListResponse in backend/sherlock/schemas/intake.py.
 */
export interface IntakeDraftListResponse {
  results: IntakeDraftSummary[];
}

/** Backend-authoritative type values (Pydantic Literal in schemas/intake.py). */
export type RequirementType = 'Bug' | 'Feature' | 'Enhancement' | 'Tech' | 'Adhoc';

export type RequirementSeverity = 'P0' | 'P1' | 'P2' | 'P3' | null;

/** Backend-authoritative size values (Pydantic Literal in schemas/intake.py). */
export type RequirementSize = 'S' | 'M' | 'L' | null;

export type RequirementBucket = 'Business' | 'Engineering Health' | null;

/** DetectOS product a requirement can target. A request may target one or both. */
export type DetectProject = 'detectOS v1' | 'detectOS v2' | 'Sherlock';
export const DETECT_PROJECTS: DetectProject[] = ['detectOS v1', 'detectOS v2', 'Sherlock'];

export interface IntakeQuestion {
  text: string;
  options: string[];
  question_type: IntakeQuestionType;
  /** Pre-selected option (e.g. the extracted Type/Severity guess). */
  default?: string | null;
  /** Optional questions never block the round's Continue button. */
  optional?: boolean;
  /** AI-tailored suggested answers for free-text questions, rendered as tap-to-fill
   *  chips. Non-binding hints; the field stays free-text and editable. */
  recommendations?: string[];
  /** The derived field this question fills (internal; ignored by the UI). */
  field?: string | null;
  /** Human-readable explanation of why Sherlock is asking this question.
   *  Shown as a muted helper line under the question to guide non-technical users. */
  rationale?: string | null;
  /** The coverage dimension this question addresses (e.g. "acceptance", "scope").
   *  Internal metadata surfaced from the backend; not displayed directly in the UI. */
  dimension?: string | null;
}

export interface RequirementPreview {
  title: string;
  problem: string;
  expected_outcome: string;
  acceptance_criteria: string[];
  type: RequirementType;
  severity?: RequirementSeverity;
  size?: RequirementSize;
  area?: string | null;
  requested_by: string;
  why_now?: string | null;
  due?: string | null;
  bucket?: RequirementBucket;
  category?: string[];
  client?: string | null;
}

export interface IntakeSessionState {
  id: string;
  status: IntakeSessionStatus;
  initial_request: string;
  projects: string[];
  round: number;
  questions: IntakeQuestion[] | null;
  derived: RequirementPreview | null;
  notion_task_id: string | null;
  notion_url: string | null;
  requirement_id: string | null;
  // Phase A additions (all have safe defaults when served by older backend rows):
  phase: string;
  steps: IntakeStepOut[];
  last_error: string | null;
  failed_step_id: string | null;
  created_at: string;
  updated_at: string;
  /** Never null from the backend; fixtures/older mocks may omit it, so read via `?? []`. */
  attachments: IntakeAttachment[];
}

export interface IntakeDedupeCandidateItem {
  requirement_id: string;
  notion_task_id: string;
  title: string;
  problem_preview: string;
  similarity: number;
  stage: string;
  status: string;
}

export interface IntakeDedupeCandidatesResponse {
  candidates: IntakeDedupeCandidateItem[];
}

export interface IntakeConfirmResponse {
  session_id: string;
  status: string;
  notion_task_id: string | null;
  requirement_id: string | null;
  notion_url: string | null;
  deduped_into: string | null;
}

export interface IntakeAnswerItem {
  question_index: number;
  value: string;
}

// ---------------------------------------------------------------------------
// Phase C — SSE event discriminated union
// Mirrors the backend SSE event vocabulary (api/intake_stream.py).
// All payloads are snake_case on the wire; reducer sets are idempotent
// (wholesale replace keyed by id/round) so catchup-replay is safe.
// ---------------------------------------------------------------------------

export interface SessionStartedPayload {
  session_id: string;
  status: string;
}

export interface StepStartedPayload {
  step_id: string;
  kind: IntakeStepKind;
  round_index: number | null;
}

/** step_progress is optional — backend may not emit it; handle defensively. */
export interface StepProgressPayload {
  step_id: string;
  kind: IntakeStepKind;
  note: string;
}

export interface CoverageUpdatePayload {
  covered: string[];
  uncovered: string[];
  required: string[];
}

export interface QuestionReadyPayload {
  round_index: number;
  questions: IntakeQuestion[];
}

/** recommend_ready always follows question_ready and backfills chips. */
export interface RecommendReadyPayload {
  round_index: number;
  questions: IntakeQuestion[];
}

export interface PreviewReadyPayload {
  derived: RequirementPreview;
}

export interface StepFailedPayload {
  step_id: string;
  kind: IntakeStepKind;
  error: string;
  error_kind: 'transient' | 'upstream' | 'internal';
  retryable: boolean;
}

export interface StreamDonePayload {
  status: string;
  session_id: string;
  notion_url: string | null;
  requirement_id: string | null;
  deduped_into: string | null;
}

/**
 * Discriminated union of all intake SSE events.
 * Use `evt.event` as the discriminant to narrow the payload type.
 */
export type IntakeSSEEvent =
  | { event: 'session_started'; data: SessionStartedPayload }
  | { event: 'step_started'; data: StepStartedPayload }
  | { event: 'step_progress'; data: StepProgressPayload }
  | { event: 'coverage_update'; data: CoverageUpdatePayload }
  | { event: 'question_ready'; data: QuestionReadyPayload }
  | { event: 'recommend_ready'; data: RecommendReadyPayload }
  | { event: 'preview_ready'; data: PreviewReadyPayload }
  | { event: 'step_failed'; data: StepFailedPayload }
  | { event: 'done'; data: StreamDonePayload };
